import React, {ChangeEvent, memo, useCallback, useEffect, useMemo, useState} from 'react';
import PageBlock from "../../../components/PageBlock";
import {Button, CardBody, Checkbox} from "@material-tailwind/react";
import {API} from "../../../types/API";
import {AxiosResponse} from "axios";
import {Input} from "@material-tailwind/react/components/Input";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../../store";
import {getError, hasError} from "../../../hooks/errors";
import {clear, validateData} from "../../../reducers/validationReducer";
import {InputType} from "../../../types/InputType";
import {useToastContext} from "../../../types/contexts/ToastContext";
import {useNavigate, useParams} from "react-router-dom";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {CountriesShortResponse, Country} from "../../../types/Responses/CountriesResponse";
import {Area} from "react-easy-crop";
import {Size} from "react-easy-crop/types";
import CropImg from "../../../components/CropImage/CropImg";
import {Shape} from "../../../components/CropImage/types/Shape";
import onUpload from "../../../components/CropImage/helpers/onUpload";
import {getCroppedImage} from "../../../components/CropImage/helpers/getCroppedImage";
import {ImageSize} from "../../../components/CropImage/types/ImageSize";
import FormSkeleton from "../../../components/Skeletons/FormSkeleton";
import {ErrorHandler} from "../../../hooks/errorHandler";
import {useErrorHandler} from "react-error-boundary";
import DropdownInput from "../../../components/DropdownInput";
import {useSystemContext} from "../../../types/contexts/SystemContext";
import {PaymentMethod, PaymentMethodTypesResponse} from "../../../types/Responses/PaymentMethodsResponse";
import CountryAvatar from "../../../components/CountryAvatar";
import {useUserContext} from "../../../types/contexts/UserContext";
import Forbidden from "../../../components/Forbidden";
import {useTranslation} from "react-i18next";
import Card from "../../../components/Card";
import {setTitle} from "../../../hooks/setTitle";
import {AllRegionsResponse} from "../../../types/Responses/RegionsResponse";
import {imageToBytes} from "../../../components/CropImage/helpers/imageToBytes";
import {APILink} from "../../../types/APILink";
import {checkEnabled2FA} from "../../../hooks/checkEnabled2FA";
import {removeDataTableRequest} from "../../../reducers/dataTableSearch";
import {setSidebarTitle} from "../../../reducers/sidebar";

const PaymentMethodForm = () => {
    const {t} = useTranslation();
    const {id} = useParams();
    const navigate = useNavigate();
    const toast = useToastContext();
    const system = useSystemContext();
    const handleError = useErrorHandler();
    const user = useUserContext();
    const [showCropper, setShowCropper] = useState<boolean>(false);

    const [types, setTypes] = useState<PaymentMethodTypesResponse>();
    const [currencies, setCurrencies] = useState<number[]>([]);
    const [countries, setCountries] = useState<number[]>([]);
    const [regions, setRegions] = useState<number[]>([]);

    const [listCountries, setListCountries] = useState<CountriesShortResponse>();
    const [allRegions, setAllRegions] = useState<AllRegionsResponse>();

    const [name, setName] = useState<string>();
    const [code, setCode] = useState<string>();
    const [status, setStatus] = useState<number>(1);
    const [provider, setProvider] = useState<number>(0);
    const [type, setType] = useState<number>(1);
    const [uploadedFile, setUploadedFile] = React.useState<string>('');
    const hiddenFileInput = React.useRef<HTMLInputElement>(null);
    const [cropped, setCropped] = React.useState<Area>();
    const [cropImageArea, setCropImageArea] = React.useState<HTMLImageElement>();
    const [isInProgress, setInProgress] = useState<boolean>(false);
    const [validated, setValidated] = useState<boolean>(false);
    const [openInNewTab, setOpenInNewTab] = useState<boolean>(false);
    const [formIsReady, setFormIsReady] = useState<boolean>(false);
    const [isForbidden, setIsForbidden] = useState<boolean>(false);
    const [withCrop, setWithCrop] = useState<boolean>(true);
    const dispatch = useDispatch();
    const errors = useSelector((state: RootState) => state.validation.errors);

    const sendTypesRequest = useCallback(() => {
        API.getPaymentMethodTypes().then((response: AxiosResponse) => {
            setTypes(response.data as PaymentMethodTypesResponse);
            if (!id) {
                setFormIsReady(true);
                setShowCropper(true);
            }
        }).catch(e => ErrorHandler(e, `get${APILink.GET_PAYMENT_METHOD_TYPES}`, toast, handleError));
    }, [toast, handleError]);

    const sendCountriesRequest = useCallback(() => {
        API.getCountriesShortList().then((response: AxiosResponse) => {
            setListCountries(response.data as CountriesShortResponse);
            if (!id) {
                setFormIsReady(true);
                setShowCropper(true);
            }
        }).catch(e => ErrorHandler(e, `get${APILink.GET_COUNTRIES_SHORT_LIST}`, toast, handleError));
    }, [toast, handleError]);

    const sendRegionsRequest = useCallback(() => {
        API.getRegionsShortList().then((response: AxiosResponse) => {
            setAllRegions(response.data as AllRegionsResponse);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_REGIONS_SHORT_LIST}`, toast, handleError));
    }, [toast, handleError]);

    const handleClick = useCallback(() => {
        // @ts-expect-error can be undefined
        hiddenFileInput.current.click();
    }, [hiddenFileInput]);

    const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const {files} = event.target;
        if (files !== null) {
            try {
                onUpload(files[0], setUploadedFile);
                setShowCropper(true);
            } catch (e) {
                // @ts-expect-error can be undefined
                toast?.show(e.message)
            }
        }
        // @ts-expect-error can be undefined
        hiddenFileInput.current.value = '';
    }, [hiddenFileInput, toast, setShowCropper, setUploadedFile]);

    const onCroppedImage = useCallback((_crop: Area, percentCrop: Area, defaultSize: Size | undefined) => {
        if (defaultSize && (percentCrop.width === 0 || percentCrop.height === 0)) {
            percentCrop = {x: 0, y: 0, ...defaultSize};
        }
        setCropped(percentCrop);
    }, [setCropped]);

    const onNewTabChange = useCallback((e: ChangeEvent<HTMLInputElement> | undefined) => {
        if (e && e.target) {
            setOpenInNewTab(parseInt(e.target.value) === 1);
        }
    }, [openInNewTab, setOpenInNewTab]);

    const changeName = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setName(e.currentTarget.value)
        }
    }, [setValidated, setName]);

    const changeCode = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setCode(e.currentTarget.value)
        }
    }, [setValidated, setCode]);

    const handleSuccessResponse = useCallback((response: AxiosResponse, message: JSX.Element | string) => {
        const country = response.data.data as Country
        if (country) {
            toast?.show(<><FontAwesomeIcon icon={solid("circle-check")}
                                           className="mr-4"/> {message}</>, "bg-teal-500");
            navigate('/app/admin/payment_methods');
        }
    }, [toast, navigate]);

    const callback = useCallback((b: boolean) => {
        setValidated(true);
        setInProgress(false);

        if (showCropper && (!cropImageArea || !cropped)) {
            toast?.show(t('admin.images.empty_error'), "bg-red-500");
            return;
        }

        if (b) {
            let request = {
                name,
                code,
                payment_type_id: type,
                provider_id: provider,
                status_id: status,
                countries: countries.filter(c => c !== null),
                currencies: currencies.filter(c => c !== null),
                open_in_new_tab: openInNewTab,
            };
            if (showCropper) {
                const iTemp = cropImageArea && cropped && withCrop ? getCroppedImage(cropImageArea, cropped, ImageSize.country) : (!withCrop ? imageToBytes(document.getElementById('non-cropped-image') as HTMLImageElement) : '');
                request = {...request, ['icon' as keyof typeof request]: iTemp};
            }
            if (id) {
                API.updatePaymentMethod(parseInt(id ?? ''), request).then((response: AxiosResponse) => handleSuccessResponse(response, t('admin.payment_methods.updated.title', {name}))).catch(e => ErrorHandler(e, `put${APILink.UPDATE_PAYMENT_METHOD.replace('$paymentMethod', id?.toString())}`, toast, handleError));
            } else {
                API.createPaymentMethod(request).then((response: AxiosResponse) => {
                    dispatch(removeDataTableRequest('/app/admin/payment_methods'));
                    handleSuccessResponse(response, t('admin.payment_methods.created.title', {name}));
                }).catch(e => ErrorHandler(e, `post${APILink.GET_PAYMENT_METHODS}`, toast, handleError));
            }

        }
    }, [setValidated, setInProgress, openInNewTab, withCrop, code, uploadedFile, countries, currencies, provider, type, status, showCropper, cropped, cropImageArea, id, handleSuccessResponse, toast, handleError, name]);

    const changeStatus = useCallback((s: string | number) => {
        setStatus(typeof s === 'string' ? parseInt(s) : s);
    }, [setStatus]);

    const changeProvider = useCallback((s: string | number) => {
        setProvider(typeof s === 'string' ? parseInt(s) : s);
    }, [setProvider]);

    const changeType = useCallback((s: string | number) => {
        setType(typeof s === 'string' ? parseInt(s) : s);
    }, [setType]);

    const changeCurrencies = useCallback((s: string | number) => {
        const newCurrencies = currencies ? [...currencies] : [];
        setCurrencies([...newCurrencies, typeof s === 'string' ? parseInt(s) : s].filter((value, index, array) => {
            return array.indexOf(value) === index;
        }));
    }, [setCurrencies, currencies]);

    const changeCountries = useCallback((s: string | number) => {
        const newCountries = countries ? [...countries] : [];
        setCountries([...newCountries, typeof s === 'string' ? parseInt(s) : s].filter((value, index, array) => {
            return array.indexOf(value) === index;
        }));
    }, [setCountries, countries]);

    const onCurrencyRemove = useCallback((index: number) => {
        if (currencies) {
            const newCurrencies = [...currencies];
            if (newCurrencies[index]) {
                newCurrencies.splice(index, 1)
            }
            setCurrencies(newCurrencies)
        }
    }, [currencies, setCurrencies]);

    const onCountryRemove = useCallback((index: number) => {
        if (countries) {
            const newCountries = [...countries];
            if (newCountries[index]) {
                newCountries.splice(index, 1)
            }
            setCountries(newCountries)
        }
    }, [countries, setCountries]);

    const changeRegions = useCallback((s: string | number) => {
        if (!allRegions)
            return;
        const newRegions = regions ? [...regions] : [];
        const filteredRegions = [...newRegions, typeof s === 'string' ? parseInt(s) : s].filter((value, index, array) => {
            return array.indexOf(value) === index;
        });
        const regionCountries = allRegions.data.filter(i => filteredRegions.includes(i.id)).map(r => r.countries);
        let newCountries = [];
        for (let i = 0; i < regionCountries.length; i++) {
            // @ts-expect-error can be undefined
            newCountries = newCountries.concat(regionCountries[i]);
        }
        setRegions(filteredRegions);
        setCountries(newCountries);
    }, [setRegions, regions, allRegions, setCountries]);

    const onRegionRemove = useCallback((index: number) => {
        if (regions && allRegions) {
            const newRegions = [...regions];
            if (newRegions[index]) {
                newRegions.splice(index, 1)
            }
            setRegions(newRegions);
            const regionCountries = allRegions.data.filter(i => newRegions.includes(i.id)).map(r => r.countries);
            let newCountries = [];
            for (let i = 0; i < regionCountries.length; i++) {
                // @ts-expect-error can be undefined
                newCountries = newCountries.concat(regionCountries[i]);
            }
            setCountries(newCountries);
        }
    }, [regions, allRegions, setRegions, setCountries]);

    const onSelectAllCountries = useCallback(() => {
        if (!listCountries)
            return;
        setCountries(listCountries.data.map(i => i.id));
    }, [listCountries, setCountries]);

    const submit = useCallback(() => {
        if (showCropper && (!cropImageArea || !cropped)) {
            toast?.show(t('admin.images.empty_error'), "bg-red-500");
            return;
        }
        setInProgress(true);
        const inputs = [
            {key: 'name', label: t('admin.payment_methods.table.name'), type: InputType.text, regex: '^.{3,}\\b$'},
            {key: 'code', label: t('admin.payment_methods.table.code'), type: InputType.text, regex: '^.{3,}$'},
            {
                key: 'provider',
                label: t('admin.payment_methods.table.provider'),
                type: InputType.text,
                regex: '^\\d{1,2}$'
            },
            {key: 'type', label: t('admin.payment_methods.table.type'), type: InputType.text, regex: '^\\d{1,1}$'},
            {key: 'status', label: t('admin.payment_methods.table.status'), type: InputType.text, regex: '^\\d{1,1}$'},
        ];
        let data = {name, code, provider, type, status, currencies, countries};
        if (showCropper) {
            const iTemp = cropImageArea && cropped ? getCroppedImage(cropImageArea, cropped, ImageSize.country) : '';
            inputs.push({
                key: 'image',
                label: t('admin.payment_methods.table.image'),
                type: InputType.text,
                regex: '^data:image/png;base64,.{32,}'
            });
            data = {...data, ['image' as keyof typeof data]: iTemp};
        }
        dispatch(validateData({
            payload: {
                inputs: inputs,
                introducedData: data,
                callback: callback
            }
        }));
    }, [setInProgress, showCropper, cropped, cropImageArea, dispatch, callback, code, name, toast]);

    const getSinglePaymentMethod = useCallback(() => {
        if (!system || !system.system || !system.system?.providers || !system.system?.statuses) {
            return;
        }
        API.getPaymentMethod(parseInt(id ?? '')).then((response: AxiosResponse) => {
            const paymentMethodResponse = response.data.data as PaymentMethod;
            setName(paymentMethodResponse.name);
            setCode(paymentMethodResponse.code);
            setUploadedFile(`${window.location.origin}/images/psps/${paymentMethodResponse.image}`);
            setCountries(paymentMethodResponse.countries ?? []);
            setCurrencies(paymentMethodResponse.currencies ?? []);
            // @ts-expect-error can be undefined
            setProvider(system.system.providers.find(i => i.name === paymentMethodResponse.provider)?.id)
            // @ts-expect-error can be undefined
            setStatus(system.system?.statuses.find(i => i.name === paymentMethodResponse.status)?.id)
            setShowCropper(false);
            setFormIsReady(true);
            setOpenInNewTab(paymentMethodResponse.open_in_new_tab ?? false);
            setTitle(t('admin.payment_methods.update.title', {name: paymentMethodResponse.name}));
            dispatch(setSidebarTitle(t('admin.payment_methods.update.title', {name: paymentMethodResponse.name})));
        }).catch(e => {
            setFormIsReady(true)
            ErrorHandler(e, `get${APILink.UPDATE_PAYMENT_METHOD.replace('$paymentMethod', id?.toString() ?? '')}`, toast, handleError, setIsForbidden)
        });
    }, [setName, setCode, setUploadedFile, setShowCropper, setFormIsReady, system, handleError, toast, id, dispatch]);

    useEffect(() => {
        if (user && user.user) {
            checkEnabled2FA(user.user).then(() => {
                sendRegionsRequest();
                sendTypesRequest();
                sendCountriesRequest();
                if (id) {
                    getSinglePaymentMethod();
                } else {
                    setTitle(t('admin.buttons.update'));
                    dispatch(setSidebarTitle(t('admin.payment_methods.create.title')));
                    if (user && !user.user.permissions.includes('create payment method')) {
                        setIsForbidden(true)
                    }
                }
            }).catch();
        }
        dispatch(clear())
    }, [dispatch, id, sendTypesRequest, sendCountriesRequest, t, user]);

    const uploadImageUI = useMemo(() => {
        return <>
            <div className="max-wmd mt-4">
                <Button className="bg-black text-white"
                        onClick={() => handleClick()}>{uploadedFile ? t('admin.images.replace') : t('admin.images.upload')}</Button>
                <input type="file" accept="image/*" ref={hiddenFileInput} onChange={handleChange}
                       style={{display: 'none'}}/>
                {validated && hasError('image', errors) &&
                    <label key={'error-image'}
                           className="mt-1 text-red-500 text-xs block">{getError('image', errors)}</label>}
            </div>

            {showCropper && <div className="max-w-md mt-4 relative">
                <CropImg setWithCropCallback={setWithCrop} shape={Shape.RECTANGLE} ratio={2} img={uploadedFile}
                         onCropCompleted={onCroppedImage} onCropLoaded={(i) => setCropImageArea(i)} withoutLeft/>
            </div>}
            {!showCropper && uploadedFile &&
                <div className="max-w-md mt-4 flex"><img src={uploadedFile} className="h-36"/></div>}
        </>
    }, [uploadedFile, withCrop, validated, errors, onCroppedImage, setCropImageArea, handleClick, handleChange, showCropper]);

    return (
        <PageBlock>
            {!formIsReady ? <FormSkeleton/> : <>
                <Card className="mt-6 bg-white dark:bg-blue-gray-900 dark:text-gray-500">
                    <CardBody className="overflow-auto px-0">
                        {!isForbidden && <>
                            <div className="max-w-md">
                                <Input success={!hasError('name', errors) && validated}
                                       error={validated && hasError('name', errors)} crossOrigin=''
                                       variant={'outlined'} label={t('admin.payment_methods.table.name')} type={'text'}
                                       onChange={changeName}
                                       value={name}
                                       className="inline-block dark:text-gray-500 dark:border-blue-gray-800 dark:placeholder-shown:border-blue-gray-800 dark:focus:border-blue-gray-800 dark:focus:border-t-transparent"
                                       labelProps={{className: "dark:after:border-blue-gray-800 dark:before:border-blue-gray-800 dark:peer-focus:text-white dark:peer-focus:before:!border-blue-gray-800 dark:peer-focus:after:!border-blue-gray-800"}}/>
                                {validated && hasError('name', errors) &&
                                    <label key={'error-name'}
                                           className="mt-1 text-red-500 text-xs block">{getError('name', errors)}</label>}
                            </div>
                            <div className="max-w-md mt-4">
                                <Input success={!hasError('code', errors) && validated}
                                       error={validated && hasError('code', errors)} crossOrigin=''
                                       variant={'outlined'} label={t('admin.payment_methods.table.code')} type={'text'}
                                       onChange={changeCode}
                                       value={code}
                                       className="inline-block dark:text-gray-500 dark:border-blue-gray-800 dark:placeholder-shown:border-blue-gray-800 dark:focus:border-blue-gray-800 dark:focus:border-t-transparent"
                                       labelProps={{className: "dark:after:border-blue-gray-800 dark:before:border-blue-gray-800 dark:peer-focus:text-white dark:peer-focus:before:!border-blue-gray-800 dark:peer-focus:after:!border-blue-gray-800"}}/>
                                {validated && hasError('code', errors) &&
                                    <label key={'error-code'}
                                           className="mt-1 text-red-500 text-xs block">{getError('code', errors)}</label>}
                            </div>
                            <div className="max-w-md mt-4">
                                <DropdownInput name={t('admin.payment_methods.table.provider')}
                                               searchable={true}
                                               selected={provider}
                                               onChange={changeProvider} list={system?.system?.providers ?? []}/>

                                {validated && hasError('provider', errors) &&
                                    <label key={'error-provider'}
                                           className="mt-1 text-red-500 text-xs block">{getError('provider', errors)}</label>}
                            </div>

                            <div className="max-w-md mt-4">
                                <DropdownInput name={t('admin.payment_methods.table.currencies')}
                                               searchable={true}
                                               multiple={true}
                                               selected={currencies}
                                               onChange={changeCurrencies}
                                               onRemove={onCurrencyRemove}
                                               list={system?.system?.currencies.map((i) => {
                                                   return {id: i.id, name: i.code}
                                               }) ?? []}/>

                                {validated && hasError('currencies', errors) &&
                                    <label key={'error-currencies'}
                                           className="mt-1 text-red-500 text-xs block">{getError('currencies', errors)}</label>}
                            </div>

                            <div className="max-w-md mt-4">
                                <DropdownInput id="regions_form" name={t('admin.payment_methods.regions')}
                                               className="mb-4"
                                               searchable={true}
                                               multiple={true}
                                               selected={regions}
                                               onChange={changeRegions}
                                               onRemove={onRegionRemove}
                                               list={allRegions?.data ?? []}/>
                            </div>

                            <div className="max-w-md mt-4">
                                <DropdownInput name={t('admin.payment_methods.table.countries')}
                                               searchable={true}
                                               multiple={true}
                                               withAllButton={true}
                                               selected={countries}
                                               onChange={changeCountries}
                                               onRemove={onCountryRemove}
                                               onSelectAll={onSelectAllCountries}
                                               list={listCountries?.data.map((item) => {
                                                   return {
                                                       id: item.id,
                                                       name: item.name,
                                                       icon: <CountryAvatar country={item} withName={false}
                                                                            className="h-3 max-w-[1rem]"/>
                                                   }
                                               }) ?? []}/>

                                {validated && hasError('countries', errors) &&
                                    <label key={'error-countries'}
                                           className="mt-1 text-red-500 text-xs block">{getError('countries', errors)}</label>}
                            </div>

                            <div className="max-w-md mt-4">
                                <div className="w-full grid grid-cols-2 gap-2">
                                    <DropdownInput className="w-[calc(50% - 2.5rem)] max-w-[14rem]"
                                                   menuClassName="max-w-[14rem] min-w-[14rem]"
                                                   name={t('admin.payment_methods.table.status')}
                                                   selected={status}
                                                   onChange={changeStatus} list={system?.system?.statuses ?? []}/>

                                    <DropdownInput className="w-[calc(50% - 2.5rem)] max-w-[14rem]"
                                                   menuClassName="max-w-[14rem] min-w-[14rem]"
                                                   name={t('admin.payment_methods.table.type')}
                                                   selected={type}
                                                   onChange={changeType} list={types?.data ?? []}/>
                                </div>

                                {validated && hasError('status', errors) &&
                                    <label key={'error-status'}
                                           className="mt-1 text-red-500 text-xs block">{getError('status', errors)}</label>}

                                {validated && hasError('type', errors) &&
                                    <label key={'error-type'}
                                           className="mt-1 text-red-500 text-xs block">{getError('type', errors)}</label>}
                            </div>

                            <div className="max-w-md mt-4">
                                <Checkbox checked={openInNewTab}
                                          onChange={onNewTabChange}
                                          color={'teal'} crossOrigin={''}
                                          value={openInNewTab ? 0 : 1} label={<span
                                    className="dark:text-gray-500">{t('admin.payment_methods.open_in_new_tab')}</span>}/>
                            </div>

                            {uploadImageUI}

                            <div className="mt-12">
                                <Button onClick={() => submit()} className="text-white bg-teal-500"
                                        disabled={isInProgress}>{id ? t('admin.buttons.update') : t('admin.buttons.create')}</Button>
                            </div>
                        </>}
                        <Forbidden isForbidden={isForbidden}/>
                    </CardBody>
                </Card>
            </>}
        </PageBlock>
    );
}
export default memo(PaymentMethodForm);
